// 最近观看 — iOS 截图复刻：列表 / 每项缩略图 + 在线 tag + 设备名 + 时间
// 路由：/m/recent
//
// 数据源：localStorage（getRecentRecords，纯客户端）
//
// 与 iOS/Android 一致：原生 app 也用本地数据库存储（反编译验证 com.ulucu.model.store.model.CStoreManager.queryStoreHistry()
//   → mStoreDatabase.queryStoreHistory()，是本地 SQLite 表，不走服务端）。
// 因此本页 localStorage 的设计与原生 app 行为完全对齐，无需接 API。
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { App as AntdApp, Button, Empty, Tag, Typography } from 'antd';
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getRecentRecordsWithApacHydration } from '../api/recent.js';
import { clearRecentRecords, deleteRecentRecord, getRecentRecords, type RecentRecord } from '../utils/recent-records.js';
import { useRegion } from '../hooks/useRegion.js';

const PRIMARY = '#FF7043';

export default function Recent() {
	const region = useRegion();
	return region === 'apac' ? <NativeRecent /> : <RecentClassic />;
}

function RecentClassic() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { message, modal } = AntdApp.useApp();
	const [records, setRecords] = useState<RecentRecord[]>([]);
	const isZh = i18n.language.startsWith('zh');

	useEffect(() => {
		setRecords(getRecentRecords());
	}, []);

	const onPlay = (r: RecentRecord) => {
		navigate(`/m/player?storeId=${r.storeId}&storeName=${encodeURIComponent(r.storeName)}&deviceSn=${r.deviceSn}&deviceLabel=${encodeURIComponent(r.deviceLabel)}&channel=${r.channel}`);
	};

	const onClear = () => {
		modal.confirm({
			title: isZh ? '清空观看历史？' : 'Clear watch history?',
			okText: isZh ? '清空' : 'Clear',
			cancelText: t('common.cancel'),
			okButtonProps: { danger: true },
			onOk: () => {
				clearRecentRecords();
				setRecords([]);
				message.success(isZh ? '已清空观看历史' : 'Watch history cleared');
			},
		});
	};

	const onDelete = (record: RecentRecord) => {
		const next = deleteRecentRecord(record.deviceSn, record.channel);
		setRecords(next);
		message.success(isZh ? '已删除该条历史' : 'History item deleted');
	};

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<div style={{
				display: 'flex', alignItems: 'center',
				padding: '12px 12px', background: '#fff',
				borderBottom: '1px solid #f0f0f0',
			}}>
				<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
				<Typography.Text strong style={{ flex: 1, textAlign: 'center', fontSize: 17 }}>
					{t('mtab.profile.recent')}
				</Typography.Text>
				<Button
					type="text"
					disabled={records.length === 0}
					onClick={onClear}
					style={{ color: records.length === 0 ? '#c0c4cc' : PRIMARY, minWidth: 44 }}
				>
					{isZh ? '清空' : 'Clear'}
				</Button>
			</div>

			{records.length === 0 ? (
				<div style={{ padding: '60px 0' }}>
					<Empty description={t('mtab.profile.recent_empty')} />
				</div>
			) : (
				<div style={{
					padding: 8,
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
					gap: 10,
				}}>
					{records.map((r) => (
						<div
							key={`${r.deviceSn}-${r.channel}`}
							onClick={() => onPlay(r)}
							style={{
								borderRadius: 8,
								overflow: 'hidden',
								background: '#000',
								cursor: 'pointer',
								position: 'relative',
								aspectRatio: '16 / 9',
							}}
						>
							{/* 缩略图 */}
							{recentThumbSrc(r) ? (
								<img
									src={recentThumbSrc(r)}
									alt={r.deviceLabel}
									style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
								/>
							) : (
								<div style={{
									width: '100%', height: '100%',
									display: 'flex', alignItems: 'center', justifyContent: 'center',
									color: '#666', fontSize: 14,
								}}>
									{r.deviceLabel}
								</div>
							)}

							{/* 左上角时间戳水印（模拟摄像头 OSD） */}
							<div style={{
								position: 'absolute', top: 8, left: 10,
								color: '#fff', fontSize: 11, fontFamily: 'monospace',
								textShadow: '1px 1px 0 rgba(0,0,0,0.6)',
							}}>
								{dayjs(r.viewedAt).format('YYYY-MM-DD ddd HH:mm:ss')}
							</div>

							{/* 右上角在线 tag */}
							{r.online !== false && (
								<Tag
									color="orange"
									style={{ position: 'absolute', top: 6, right: 6, margin: 0, borderRadius: 12, padding: '0 8px' }}
								>
									● {t('mtab.profile.online')}
								</Tag>
							)}

							<button
								type="button"
								aria-label={isZh ? '删除该条历史' : 'Delete this history item'}
								onClick={(event) => {
									event.stopPropagation();
									onDelete(r);
								}}
								style={{
									position: 'absolute',
									top: 6,
									right: r.online !== false ? 74 : 6,
									width: 32,
									height: 32,
									border: 0,
									borderRadius: 16,
									background: 'rgba(0,0,0,0.48)',
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									cursor: 'pointer',
								}}
							>
								<DeleteOutlined />
							</button>

							{/* 底部文字层（store/设备 + 时间） */}
							<div style={{
								position: 'absolute',
								bottom: 0, left: 0, right: 0,
								padding: '6px 10px',
								background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
								color: '#fff',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-end',
								fontSize: 13,
							}}>
								<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
									{r.storeName} / {r.deviceLabel}
								</span>
								<span style={{ fontSize: 11, fontFamily: 'monospace', marginLeft: 8 }}>
									{dayjs(r.viewedAt).format('YYYY/MM/DD HH:mm:ss')}
								</span>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

void PRIMARY;

function NativeRecent() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [records, setRecords] = useState<RecentRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [lastUpdate, setLastUpdate] = useState<number | null>(null);

	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const load = async () => {
		setLoading(true);
		const next = await getRecentRecordsWithApacHydration().catch(() => getRecentRecords());
		setRecords(next);
		setLastUpdate(Date.now());
		setLoading(false);
	};

	const onPlay = (r: RecentRecord) => {
		navigate(`/m/player?storeId=${r.storeId}&storeName=${encodeURIComponent(r.storeName)}&deviceSn=${r.deviceSn}&deviceLabel=${encodeURIComponent(r.deviceLabel)}&channel=${r.channel}`);
	};

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.profile.watch_history')} onBack={() => navigate(-1)} />
			<NativeRefreshStatus loading={loading} lastUpdate={lastUpdate} />
			{records.length === 0 ? (
				<NativeEmpty onRefresh={() => void load()} />
			) : (
				<div style={{ paddingTop: 12 }}>
					{records.map((r) => (
						<button
							key={`${r.deviceSn}-${r.channel}`}
							type="button"
							onClick={() => onPlay(r)}
							style={{
								width: '100%',
								border: 0,
								padding: 0,
								margin: '0 0 12px',
								background: '#000',
								position: 'relative',
								aspectRatio: '16 / 9',
								display: 'block',
								overflow: 'hidden',
								cursor: 'pointer',
							}}
						>
							{recentThumbSrc(r) ? (
								<img src={recentThumbSrc(r)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
							) : (
								<div style={{ width: '100%', height: '100%', background: '#d8d8d8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: 15, padding: 12 }}>
									<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.deviceLabel}</span>
								</div>
							)}
							{r.online !== false && (
								<span
									style={{
										position: 'absolute',
										top: 12,
										right: 12,
										padding: '2px 10px',
										borderRadius: 0,
										background: '#ff8a34',
										color: '#fff',
										fontSize: 13,
										lineHeight: 1.4,
									}}
								>
									{t('native.common.online')}
								</span>
							)}
							<div
								style={{
									position: 'absolute',
									left: 0,
									right: 0,
									bottom: 0,
									height: 54,
									background: 'rgba(0,0,0,0.48)',
									display: 'flex',
									alignItems: 'center',
									padding: '0 22px',
									color: '#fff',
									fontSize: 17,
									textAlign: 'start',
								}}
							>
								<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{r.storeName}/{r.deviceLabel}
								</span>
								<span style={{ marginLeft: 12, flex: '0 0 auto' }}>{dayjs(r.viewedAt).format('DD/MM/YYYY')}</span>
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

function recentThumbSrc(record: RecentRecord): string | undefined {
	return record.thumbDataUrl || record.thumbUrl;
}

function NativeHeader({ title, onBack }: { title: string; onBack: () => void }) {
	const { t } = useTranslation();
	return (
		<header
			style={{
				height: 48,
				background: '#fff',
				borderBottom: '1px solid #eeeeee',
				display: 'grid',
				gridTemplateColumns: '56px minmax(0, 1fr) 56px',
				alignItems: 'center',
				position: 'sticky',
				top: 0,
				zIndex: 10,
			}}
		>
			<button
				type="button"
				aria-label={t('common.back')}
				onClick={onBack}
				style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 18 }}
			>
				<LeftOutlined />
			</button>
			<div style={{ textAlign: 'center', color: '#20242b', fontSize: 16, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{title}
			</div>
			<span />
		</header>
	);
}

function NativeRefreshStatus({ loading, lastUpdate }: { loading: boolean; lastUpdate: number | null }) {
	return (
		<div style={{ height: 112, background: '#fff', borderBottom: '1px solid #eeeeee', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
			<div>
				<div style={{ color: '#111', fontSize: 15, lineHeight: 1.3 }}>
					{loading ? 'Refreshing...' : 'Refresh Successfully'}
				</div>
				<div style={{ marginTop: 8, color: '#9a9a9a', fontSize: 15, lineHeight: 1.2 }}>
					{lastUpdate ? `Last Update: ${dayjs(lastUpdate).format('DD/MM/YYYY HH:mm:ss')}` : ''}
				</div>
			</div>
		</div>
	);
}

function NativeEmpty({ onRefresh }: { onRefresh: () => void }) {
	const { t } = useTranslation();
	return (
		<div style={{ paddingTop: 180, color: '#9a9a9a', textAlign: 'center', fontSize: 14 }}>
			<div>{t('native.common.no_data')}</div>
			<button
				type="button"
				onClick={onRefresh}
				style={{ marginTop: 12, border: 0, background: 'transparent', color: '#ff8a34', fontSize: 15 }}
			>
				{t('native.common.refresh')}
			</button>
		</div>
	);
}
